import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Opportunity from "@/services/opportunity/opportunity";
import type { RegisterOpportunity } from "@/dtos/Opportunity.dto";

export interface RegisterOpportunityParams {
  name?: string;
  confidence: string;
  expectedCloseDate: Date | string;
  dealType: string;
  contactId?: string;
  contactIds?: string[];
  ownerId: string;
  opportunityStageId: string;
  organizationId?: string;
  leadSourceId?: string;
  products?: any[];
  status?: string;
}

export function useRegisterOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RegisterOpportunityParams) => {
      // Validate required fields
      if (!params.name) {
        throw new Error("Deal Name is required");
      }
      if (!params.opportunityStageId && !params.status) {
        throw new Error("Opportunity Stage is required");
      }
      if (!params.leadSourceId) {
        throw new Error("Lead Source is required");
      }
      if (!params.confidence) {
        throw new Error("Confidence is required");
      }
      if (!params.expectedCloseDate) {
        throw new Error("Expected Close Date is required");
      }
      if (!params.dealType) {
        throw new Error("Deal Type is required");
      }
      if (!params.ownerId) {
        throw new Error("Owner is required");
      }

      // Transform params to match RegisterOpportunity DTO
      const payload: RegisterOpportunity = {
        name: params.name,
        status: params.status || params.opportunityStageId || "",
        confidence: params.confidence,
        expectedCloseDate: params.expectedCloseDate as Date,
        owner: params.ownerId,
        dealType: params.dealType,
        ...(params.contactId ? { contactId: params.contactId } : {}),
        contactIds: params.contactIds,
        organizationId: params.organizationId,
        leadSourceId: params.leadSourceId,
        products: params.products,
      };

      const result = await Opportunity.registerOpportunity(payload);
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to register opportunity");
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityStages"] });
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });
}
